// src/lib/reject-reason.ts
//
// The rejection reason's rules, in the browser, so the officer is told them
// while typing instead of discovering them as a 400 after pressing the button.
//
// ══ ★★ S-067 CLOSED — THIS MODULE NO LONGER OWNS THE NUMBER ══
//
// It used to, and its own header described the arrangement rather than defending
// it: *"This module is the FOURTH place that figure appears… **If the gateway's
// constant moves, this one moves with it**"* — a control that consists of somebody
// remembering. `REJECT_REASON_MAX_LENGTH = 1000` is DELETED.
//
// Every function below now takes the bound as a PARAMETER, and both screens pass
// the value the SERVER published on the read they already make: `validation.reason`
// on `GET /api/bills` and on `GET /api/bills/:reference`, declared once in
// `services/gateway/src/routes/bills-field-bounds.ts` — the same `validation` shape
// the thirteen administration catalogues have used since Wave 8 Task 3.
//
// ⚠ What stays here is the RULES, and that distinction is the whole design. P2C11
// §14.6 requires validation *"at the server end, and where applicable at the client
// end"*, so a client that says nothing is not compliant — but nothing in the clause
// licenses the client to hold its own COPY of the figure. The rules (what counts as
// empty, which length is measured, what the officer is told) are presentation and
// belong here; the NUMBER is enforcement and belongs to the server.
//
// ⚠ **An ABSENT bound is not a licence to invent one.** If the server publishes no
// `validation` block, these functions bound nothing and the officer is told nothing
// about length — the server still refuses, so the request is safe, and the screen
// simply cannot pre-empt it. Substituting a remembered 1,000 there would recreate
// the finding in the one place nobody would look for it.
//
// ══ WHY THE CLIENT MEASURES THE RAW STRING, NOT THE TRIMMED ONE ══
//
// The server bounds BOTH, at different layers and on different values: the
// route's JSON schema caps the RAW body at 1,000, and the handler caps the
// TRIMMED value at 1,000. So a 1,004-character string ending in four spaces
// trims to 1,000 and would satisfy the handler — but never reaches it, because
// the schema refused the raw body first. Measuring the raw length is the only
// client rule that satisfies both layers, and it is the conservative direction:
// it can only ever stop a request the schema was going to reject anyway.
//
// Emptiness, conversely, is judged on the TRIMMED value, because that is what
// the handler judges: `"   "` passes a `minLength: 1` schema and is exactly
// what the handler's trim catches. The migration's own note makes the same
// point.

export type RejectReasonProblem =
  /** Nothing but whitespace — the handler's own first refusal. */
  | { readonly kind: 'empty' }
  /** Over the bound. Carries both numbers so the message can state them. */
  | { readonly kind: 'too-long'; readonly length: number; readonly max: number }

/**
 * What is wrong with this reason, or `null` when the server will accept it.
 *
 * Takes the RAW field value — trimming is this function's job, not the
 * caller's, so every call site judges emptiness and length the same way.
 */
export function rejectReasonProblem(raw: string, max: number | undefined): RejectReasonProblem | null {
  // ⚠ `max === undefined` means the SERVER published no bound on this read, so
  // this function bounds nothing rather than substituting a number. Emptiness is
  // still judged: that rule is the handler's own first refusal and needs no figure.
  if (max !== undefined && raw.length > max) {
    return { kind: 'too-long', length: raw.length, max }
  }
  if (raw.trim().length === 0) return { kind: 'empty' }
  return null
}

/** True when this reason is ready to send. */
export function isSendableRejectReason(raw: string, max: number | undefined): boolean {
  return rejectReasonProblem(raw, max) === null
}

/**
 * The message shown under the field.
 *
 * `null` for an empty field that has not been touched — a required field that
 * shouts "required" before the officer has had a chance to type is noise, and
 * the hint text already says so. The Confirm button stays disabled either way,
 * so the state is never ambiguous.
 */
export function rejectReasonMessage(problem: RejectReasonProblem | null): string | null {
  if (problem === null) return null
  switch (problem.kind) {
    case 'empty':
      return 'Enter a reason. This is what the healthcare service provider will be told, so a rejection cannot be recorded without one.'
    case 'too-long':
      return `A reason may be at most ${problem.max} characters; this one is ${problem.length}. Shorten it: the full detail belongs in the case notes, not in the line the provider is shown.`
  }
}

/**
 * The always-visible hint under the field, before anything is wrong.
 *
 * ⚠ A FUNCTION now, not a constant, because the ceiling it quotes is the server's.
 * With no published bound it says everything it still truthfully can and omits the
 * number — a hint that invented one would be the finding in prose.
 */
export function rejectReasonHint(max: number | undefined): string {
  const ceiling = max === undefined ? '' : ` Up to ${max} characters.`
  return `Required.${ceiling} This is what the healthcare service provider will be told, and it is written to the permanent transition ledger.`
}

/**
 * ★★ WAVE 9 — the hint for Annex A 37/38's approval COMMENT.
 *
 * ⚠ Its own sentence rather than a reworded reason hint, because the two fields
 * differ in every way that matters to the officer: a comment is OPTIONAL, it is not
 * what the provider is told, and it is not the ground of a refusal. A shared string
 * with the words swapped would eventually be edited for one field and wrong for the
 * other.
 */
export function officerCommentHint(max: number | undefined): string {
  const ceiling = max === undefined ? '' : ` Up to ${max} characters.`
  return `Optional.${ceiling} Recorded against this decision in the permanent transition ledger, and visible to anyone who may read the bill's history.`
}
